import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import jobApplicationService from '../../../services/jobApplicationService';
import NotificationToast from '../../../components/ui/NotificationToast';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';

const AddApplicantModal = ({ isOpen, onClose, onApplicantAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumePreview, setResumePreview] = useState(null);
  const [formData, setFormData] = useState({
    applicant_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    status: 'pending',
    applied_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(file.type) && !['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
        setError('Please upload a valid resume file (.pdf, .doc, .docx, .txt)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Resume file size must be less than 10MB');
        return;
      }
      setResumeFile(file);
      setResumePreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumePreview) {
      URL.revokeObjectURL(resumePreview);
    }
    setResumePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.applicant_name.trim()) {
      setError('Applicant name is required');
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    
    if (!formData.position.trim()) {
      setError('Position is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let uploadedResumeUrl = null;
      if (resumeFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', resumeFile);
        formDataUpload.append('type', 'resume');
        
        try {
          const uploadResponse = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formDataUpload,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('erp_auth_token')}`
            }
          });
          
          if (uploadResponse.ok) {
            const uploadResult = await uploadResponse.json();
            uploadedResumeUrl = uploadResult.url || uploadResult.data?.url;
          } else {
            console.warn('Direct upload endpoint issue, will attempt fallback');
          }
        } catch (uploadError) {
          console.error('Resume upload error:', uploadError);
        }
      }

      const payload = {
        ...formData,
        resume_url: uploadedResumeUrl
      };

      const result = await jobApplicationService.createApplication(payload);

      if (result?.success) {
        setNotification({ 
          message: uploadedResumeUrl 
            ? 'Applicant created & resume auto-scored by ATS!' 
            : 'Applicant added successfully', 
          type: 'success' 
        });
        setTimeout(() => {
          onApplicantAdded?.(result.data);
          handleClose();
        }, 800);
      } else {
        setError(result?.error || 'Failed to add applicant');
      }
    } catch (error) {
      setError('An unexpected error occurred');
      console.log('Applicant creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      applicant_name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      status: 'pending',
      applied_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    removeResume();
    setError(null);
    setNotification(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <NotificationToast
          message={notification?.message}
          type={notification?.type}
          onDismiss={() => setNotification(null)}
        />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Add New Applicant</h2>
              <p className="text-sm text-muted-foreground">Create application with automatic ATS scoring</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 text-destructive">
                  <Icon name="AlertCircle" size={16} />
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Applicant Name *
                </label>
                <Input
                  value={formData.applicant_name}
                  onChange={(e) => handleInputChange('applicant_name', e.target.value)}
                  placeholder="Enter applicant's full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="applicant@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+91-9876543210"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Position Applied For *
                </label>
                <Input
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  placeholder="e.g., Software Engineer, Facility Manager"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Department
                </label>
                <Input
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  placeholder="e.g., Engineering, Operations, Security"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleInputChange('status', value)}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'screening', label: 'Screening' },
                    { value: 'interview', label: 'Interview' },
                    { value: 'selected', label: 'Selected' },
                    { value: 'rejected', label: 'Rejected' }
                  ]}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Application Date
                </label>
                <Input
                  type="date"
                  value={formData.applied_date}
                  onChange={(e) => handleInputChange('applied_date', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Resume Upload (with auto-ATS score note) */}
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground">
                  Resume / CV (Automatic ATS Match)
                </label>
                <span className="inline-flex items-center gap-1 text-xs text-primary font-medium">
                  <Icon name="Sparkles" size={12} />
                  Auto ATS Scoring
                </span>
              </div>
              {!resumeFile ? (
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="ats-resume-upload"
                    accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={handleResumeChange}
                    className="hidden"
                  />
                  <label htmlFor="ats-resume-upload" className="cursor-pointer">
                    <Icon name="Upload" size={24} className="mx-auto text-primary mb-2" />
                    <p className="text-sm font-medium text-foreground">
                      Click to upload resume (.pdf, .doc, .docx)
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Resume will be automatically parsed and scored against job requirements
                    </p>
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={24} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{resumeFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(resumeFile.size / 1024).toFixed(1)} KB · Will calculate ATS score on save
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={removeResume}
                    className="text-destructive hover:text-destructive"
                  >
                    <Icon name="Trash2" size={18} />
                  </Button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Additional notes about the applicant..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              <Icon name="Save" size={16} />
              Add Applicant
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddApplicantModal;


